<template>
  <div class="content terms-page">
    <div class="terms-shell">
      <div class="terms-header">
        <h1 class="page-title">약관/방침 편집</h1>
        <p class="page-sub" v-if="document">
          {{ document.locale }} · {{ document.type }} · 현재
          <strong class="v-main">{{ document.currentVersionLabel || "v" + document.currentVersionNo }}</strong>
          <span v-if="document.currentVersionLabel" class="v-sub">v{{ document.currentVersionNo }}</span>
          · 상태
          <span :class="['status', statusClass(document.status)]" style="margin-left: 4px">{{ statusLabel(document.status) }}</span>
        </p>
        <p class="page-sub" v-else>로딩 중...</p>
      </div>

      <div class="toolbar">
        <div class="toolbar-l">
          <button class="btn" @click="goList">목록</button>
          <button class="btn" @click="goVersions">버전 이력</button>
        </div>
      </div>

      <!-- FR-052: termsPolicies 는 termsUse/privacyTerms publish 시 자동 합성. 직접 편집 불가 안내. -->
      <el-alert
        v-if="isAutoComposed"
        type="info"
        :closable="false"
        show-icon
        title="이 문서는 자동 합성됩니다"
        description="termsPolicies 는 termsUse / privacyTerms (일본어는 specifiedCommerce 포함) 가 발행될 때 자동으로 다시 합성됩니다. 어드민에서 직접 임시저장 / 발행할 수 없습니다."
        class="terms-alert"
      />

      <div class="title-row" v-if="document">
        <label class="title-label">제목</label>
        <input v-model="title" :disabled="isAutoComposed" placeholder="예: 마케팅 정보 수신 동의" class="title-input" />
      </div>

      <div class="split">
        <!-- Editor card -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">본문 편집</div>
            <div class="card-meta" v-if="document">{{ document.locale }} · {{ document.type }} · {{ versionTransitionLabel }}</div>
          </div>
          <div class="card-toolbar">
            <button class="btn btn-sm" type="button" title="굵게" @click="exec('bold')"><b>B</b></button>
            <button class="btn btn-sm" type="button" title="기울임" @click="exec('italic')"><i>I</i></button>
            <button class="btn btn-sm" type="button" title="밑줄" @click="exec('underline')"><u>U</u></button>
            <button class="btn btn-sm" type="button" @click="execHeading('H2')">H2</button>
            <button class="btn btn-sm" type="button" @click="execHeading('H3')">H3</button>
            <button class="btn btn-sm" type="button" @click="exec('insertUnorderedList')">• 목록</button>
            <button class="btn btn-sm" type="button" @click="exec('insertOrderedList')">1. 목록</button>
            <button class="btn btn-sm" type="button" @click="execLink">링크</button>
            <button class="btn btn-sm" type="button" @click="execHr">— 구분선</button>
            <button class="btn btn-sm" type="button" @click="exec('removeFormat')">서식제거</button>
          </div>
          <div ref="editor" class="editor-area" :contenteditable="!isAutoComposed" @input="onEditorInput" @blur="onEditorInput"></div>
        </div>

        <!-- Preview card -->
        <div class="card">
          <div class="card-header">
            <div class="card-title">미리보기</div>
            <div class="card-meta">실시간 (sandbox)</div>
          </div>
          <iframe class="preview-frame" :srcdoc="previewSrcDoc" sandbox="allow-same-origin" referrerpolicy="no-referrer"></iframe>
          <div class="card-footer-meta" v-if="document">
            latest URL:
            <code>https://static.blip.kr/terms/{{ document.locale }}/{{ document.type }}.html</code>
          </div>
        </div>
      </div>

      <div class="sticky-actions">
        <div class="sticky-meta">
          <!-- RETRY 19 — dirty indicator uses its own .save-state class (orange = unsaved,
               gray = saved) so it does NOT collide with the publish status dot palette
               (green=PUBLISHED, gray=NEVER_PUBLISHED, orange=PUBLISHING, red=FAILED). -->
          <span class="save-state" :class="{ 'is-dirty': dirty, 'is-saved': !dirty }">
            <span class="save-dot"></span>
            {{ dirty ? "미저장 변경 있음" : "저장됨" }}
          </span>
          <span class="muted" v-if="autoSavedAt"> · 자동저장 {{ formatTime(autoSavedAt) }}</span>
          <span class="muted" v-else-if="document && document.draftUpdatedAt"> · 마지막 저장 {{ formatTime(document.draftUpdatedAt) }}</span>
          <!-- L2 — Hide auto-save schedule line for termsPolicies (auto-composed, never auto-saved). -->
          <span class="muted" v-if="!isAutoComposed"> · 자동 저장 30초마다</span>
          <span class="muted" v-else> · 자동 저장 비활성 (자동 합성 문서)</span>
        </div>
        <div class="group">
          <button
            class="btn-text danger btn-sm"
            :disabled="isDeleting || (document && document.status === 'PUBLISHED')"
            :title="document && document.status === 'PUBLISHED' ? '발행 이력이 있어 삭제할 수 없습니다' : ''"
            @click="openDeleteDialog"
          >
            삭제
          </button>
          <button class="btn" :disabled="isSaving || isAutoComposed" @click="onSaveDraft">임시저장</button>
          <button class="btn btn-primary" :disabled="isPublishing || isAutoComposed" @click="openPublishDialog">게시...</button>
        </div>
      </div>
    </div>

    <el-dialog :visible.sync="publishVisible" width="480px" title="게시 확인" :append-to-body="true" custom-class="terms-dialog">
      <p class="muted dialog-paragraph">발행하면 즉시 latest URL이 갱신됩니다. 이전 발행 본문은 버전 이력에서 rollback 가능합니다.</p>
      <p class="dialog-transition" v-if="document">{{ versionTransitionLabel }}</p>
      <el-form label-position="top">
        <el-form-item label="버전 라벨 (선택, 비우면 자동 minor++)">
          <el-input v-model="publishVersionLabel" :placeholder="publishLabelPlaceholder" />
          <div class="dialog-hint">{{ publishLabelHint }}</div>
        </el-form-item>
        <el-form-item label="발행 노트 (선택)">
          <el-input type="textarea" v-model="publishNote" :rows="3" placeholder="이번 발행에 대한 메모" />
        </el-form-item>
      </el-form>
      <div v-if="isPublishing" class="dialog-progress">
        <i class="el-icon-loading"></i>
        발행 진행 중...
      </div>
      <span slot="footer">
        <button class="btn" :disabled="isPublishing" @click="publishVisible = false">취소</button>
        <button class="btn btn-primary" :disabled="isPublishing" @click="onPublishConfirm">발행</button>
      </span>
    </el-dialog>

    <!-- M2 — Delete confirmation dialog (replaces window.confirm).
         Status-aware messaging: PUBLISHED docs are blocked at the server, so
         the dialog disables the destructive action and explains why. -->
    <el-dialog :visible.sync="deleteVisible" width="400px" title="약관 삭제 확인" :append-to-body="true" custom-class="terms-dialog">
      <p class="muted dialog-paragraph" v-if="document">
        이 약관(<b>{{ document.locale }} / {{ document.type }}</b
        >)을 삭제하시겠습니까?
      </p>
      <p v-if="document && document.status === 'PUBLISHED'" class="dialog-warning">⚠ 발행 이력이 있어 서버에서 차단됩니다.</p>
      <p v-else class="muted">미발행 상태이므로 삭제 가능합니다. 이 작업은 되돌릴 수 없습니다.</p>
      <span slot="footer">
        <button class="btn" :disabled="isDeleting" @click="deleteVisible = false">취소</button>
        <button class="btn btn-primary danger-primary" :disabled="isDeleting || (document && document.status === 'PUBLISHED')" @click="onDeleteConfirm">
          삭제
        </button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import { Alert, Dialog, Form, FormItem, Input } from "element-ui";
import { formatDateTime } from "@/util/DateUtils";
import { showAPIErrorToast } from "@/util/ErrorUtils";

const AUTO_SAVE_INTERVAL_MS = 30000;
const PREVIEW_DEBOUNCE_MS = 300;

export default {
  components: {
    [Alert.name]: Alert,
    [Dialog.name]: Dialog,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Input.name]: Input,
  },
  async created() {
    this.termsId = this.$route.params.id;
    this.installInterceptor();
    await this.fetchDocument();
    this.startAutoSave();
  },
  beforeDestroy() {
    this.stopAutoSave();
    this.uninstallInterceptor();
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
  },
  data() {
    return {
      termsId: null,
      document: null,
      title: "",
      draftBodyHtml: "",
      previewSrcDoc: "",
      previewTimer: null,
      isSaving: false,
      isPublishing: false,
      isDeleting: false,
      publishVisible: false,
      deleteVisible: false,
      publishNote: "",
      publishVersionLabel: "",
      autoSaveTimer: null,
      autoSavedAt: null,
      lastSavedSnapshot: { title: "", draftBodyHtml: "" },
      interceptorId: null,
    };
  },
  computed: {
    // FR-052: termsPolicies 는 어드민에서 직접 편집/발행 불가. termsUse/privacyTerms publish 시 자동 합성.
    isAutoComposed() {
      return !!(this.document && this.document.type === "termsPolicies");
    },
    dirty() {
      return this.title !== this.lastSavedSnapshot.title || this.draftBodyHtml !== this.lastSavedSnapshot.draftBodyHtml;
    },
    versionTransitionLabel() {
      if (!this.document) return "";
      const cur = this.document.currentVersionLabel;
      if (!cur) {
        return "신규 v1 (자동)";
      }
      const next = this.predictNextLabel(cur);
      if (next) {
        return `v${cur} → v${next} (자동)`;
      }
      return `v${cur} → 직접 입력 필요`;
    },
    // H2 — placeholder/hint help operator know exactly what label will be
    // assigned if they leave the input blank. Branches on semver vs non-semver
    // current label so the user is not misled by a stale "1.8" hint when the
    // last label was non-semver and auto-increment will not run.
    publishLabelPlaceholder() {
      if (!this.document) return "예: 1.0";
      const lastLabel = this.document.currentVersionLabel;
      if (!lastLabel) return "예: 1.0";
      const next = this.predictNextLabel(lastLabel);
      if (next) return `예: ${next}`;
      return "예: 1.0 (이전 라벨이 비semver라 직접 입력 필요)";
    },
    publishLabelHint() {
      const lastLabel = this.document && this.document.currentVersionLabel;
      if (!lastLabel) {
        return "이 문서에 라벨이 없으니 직접 입력하거나 비워두세요.";
      }
      const next = this.predictNextLabel(lastLabel);
      if (next) {
        return `이전 라벨이 ${lastLabel} (semver) → 비우면 자동으로 ${next}`;
      }
      return `이전 라벨이 ${lastLabel} (비semver) → 자동 증가 불가, 라벨 직접 입력 필요`;
    },
  },
  watch: {
    draftBodyHtml() {
      this.schedulePreviewUpdate();
    },
    title() {
      this.schedulePreviewUpdate();
    },
  },
  methods: {
    statusLabel(status) {
      const map = {
        NEVER_PUBLISHED: "미발행",
        PUBLISHED: "발행됨",
        PUBLISHING: "발행 중",
        FAILED: "실패",
      };
      return map[status] || status;
    },
    statusClass(status) {
      switch (status) {
        case "PUBLISHED":
          return "published";
        case "NEVER_PUBLISHED":
          return "never-published";
        case "PUBLISHING":
          return "publishing";
        case "FAILED":
          return "failed";
        default:
          return "never-published";
      }
    },
    predictNextLabel(label) {
      // semver 패턴 (X.Y 또는 X.Y.Z) 이면 minor++ 미리보기
      const m = /^(\d+)\.(\d+)(\.(\d+))?$/.exec(label || "");
      if (!m) return null;
      const major = m[1];
      const minor = parseInt(m[2], 10) + 1;
      return `${major}.${minor}`;
    },
    formatTime(value) {
      if (!value) return "—";
      return formatDateTime(value);
    },
    async fetchDocument() {
      try {
        const res = await this.$axios.get(`/terms/${this.termsId}`);
        const doc = res.data.data || res.data;
        this.document = doc;
        this.title = doc.title || "";
        this.draftBodyHtml = doc.draftBodyHtml || "";
        this.lastSavedSnapshot = { title: this.title, draftBodyHtml: this.draftBodyHtml };
        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.innerHTML = this.draftBodyHtml;
          }
          this.schedulePreviewUpdate();
        });
      } catch (err) {
        showAPIErrorToast(err);
      }
    },
    onEditorInput() {
      if (this.$refs.editor) {
        this.draftBodyHtml = this.$refs.editor.innerHTML;
      }
    },
    schedulePreviewUpdate() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
      }
      this.previewTimer = setTimeout(() => {
        this.previewSrcDoc = this.buildPreviewSrcDoc(this.title, this.draftBodyHtml);
      }, PREVIEW_DEBOUNCE_MS);
    },
    buildPreviewSrcDoc(title, bodyHtml) {
      const safeTitle = String(title || "")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const head = `<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${safeTitle}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;padding:16px;color:#222;line-height:1.6;}h1{font-size:1.4em;}h2{font-size:1.2em;}</style>`;
      return `<!DOCTYPE html><html><head>${head}</head><body>${bodyHtml || ""}</body></html>`;
    },
    exec(command) {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
      document.execCommand(command, false, null);
      this.onEditorInput();
    },
    execHeading(tagName) {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
      document.execCommand("formatBlock", false, tagName);
      this.onEditorInput();
    },
    execLink() {
      const url = window.prompt("링크 URL을 입력하세요 (https://...)");
      if (!url) return;
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
      document.execCommand("createLink", false, url);
      this.onEditorInput();
    },
    execHr() {
      if (this.$refs.editor) {
        this.$refs.editor.focus();
      }
      document.execCommand("insertHorizontalRule", false, null);
      this.onEditorInput();
    },
    async onSaveDraft() {
      if (!this.termsId) return;
      if (this.isAutoComposed) {
        window.Toast.warning("termsPolicies 는 자동 합성 문서이므로 임시저장할 수 없습니다.");
        return;
      }
      this.isSaving = true;
      try {
        const body = {
          title: this.title,
          draftBodyHtml: this.draftBodyHtml,
        };
        await this.$axios.put(`/terms/${this.termsId}/draft`, body);
        this.lastSavedSnapshot = { title: this.title, draftBodyHtml: this.draftBodyHtml };
        this.autoSavedAt = new Date().toISOString();
        window.Toast.success("임시저장되었습니다");
        await this.fetchDocument();
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isSaving = false;
      }
    },
    startAutoSave() {
      this.stopAutoSave();
      this.autoSaveTimer = setInterval(() => {
        if (!this.termsId) return;
        if (this.isSaving || this.isPublishing) return;
        // FR-052: termsPolicies 자동 합성 문서는 임시저장 차단 — auto-save 도 동일하게 스킵
        if (this.isAutoComposed) return;
        if (!this.dirty) return;
        this.silentAutoSave();
      }, AUTO_SAVE_INTERVAL_MS);
    },
    stopAutoSave() {
      if (this.autoSaveTimer) {
        clearInterval(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
    },
    async silentAutoSave() {
      try {
        const body = {
          title: this.title,
          draftBodyHtml: this.draftBodyHtml,
        };
        await this.$axios.put(`/terms/${this.termsId}/draft`, body);
        this.lastSavedSnapshot = { title: this.title, draftBodyHtml: this.draftBodyHtml };
        this.autoSavedAt = new Date().toISOString();
      } catch (err) {
        // 자동 저장 실패는 인터셉터의 5xx toast로 가시화. silent.
      }
    },
    async openPublishDialog() {
      if (this.isAutoComposed) {
        window.Toast.warning("termsPolicies 는 자동 합성 문서이므로 발행할 수 없습니다.");
        return;
      }
      // H1 — Sync editor DOM → draftBodyHtml first. The editor's @blur handler
      // would normally fire on click-out, but click → mousedown on the publish
      // button can race the blur, leaving draftBodyHtml stale and (more
      // importantly) `dirty` true even when the user has no real unsaved
      // changes beyond what blur would have already captured. Calling
      // onEditorInput() explicitly + awaiting nextTick lets Vue settle the
      // computed before we evaluate `dirty` below.
      this.onEditorInput();
      await this.$nextTick();

      if (this.dirty) {
        const ok = window.confirm("저장되지 않은 변경이 있습니다. 임시저장하고 발행하시겠습니까?");
        if (!ok) return;
        await this.onSaveDraft();
        if (this.dirty) return; // save failed (snapshot did not advance)
      }
      this.publishNote = "";
      this.publishVersionLabel = "";
      this.publishVisible = true;
    },
    async onPublishConfirm() {
      if (!this.termsId) return;
      if (this.isAutoComposed) return;
      this.isPublishing = true;
      try {
        const body = {};
        if (this.publishNote) {
          body.note = this.publishNote;
        }
        if (this.publishVersionLabel && this.publishVersionLabel.trim()) {
          body.versionLabel = this.publishVersionLabel.trim();
        }
        await this.$axios.post(`/terms/${this.termsId}/publish`, body);
        window.Toast.success("발행되었습니다");
        this.publishVisible = false;
        await this.fetchDocument();
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isPublishing = false;
      }
    },
    openDeleteDialog() {
      if (!this.termsId) return;
      this.deleteVisible = true;
    },
    async onDeleteConfirm() {
      if (!this.termsId) return;
      // Defensive — the destructive button is disabled for PUBLISHED docs,
      // but recheck client-side before issuing the DELETE.
      if (this.document && this.document.status === "PUBLISHED") return;
      this.isDeleting = true;
      try {
        await this.$axios.delete(`/terms/${this.termsId}`);
        window.Toast.success("삭제되었습니다");
        this.deleteVisible = false;
        this.$router.push("/terms/list");
      } catch (err) {
        showAPIErrorToast(err);
      } finally {
        this.isDeleting = false;
      }
    },
    goList() {
      this.$router.push("/terms/list");
    },
    goVersions() {
      this.$router.push(`/terms/${this.termsId}/versions`);
    },
    installInterceptor() {
      this.interceptorId = this.$axios.interceptors.response.use(
        (response) => response,
        (error) => {
          const status = error && error.response && error.response.status;
          if (status >= 500 && status < 600) {
            window.Toast.error("서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
          }
          return Promise.reject(error);
        }
      );
    },
    uninstallInterceptor() {
      if (this.interceptorId !== null && this.interceptorId !== undefined) {
        this.$axios.interceptors.response.eject(this.interceptorId);
        this.interceptorId = null;
      }
    },
  },
};
</script>
<style scoped>
/* .terms-page wrapper limits readability tweaks (font-size + line-height) to Terms pages.
   Fluid scaling via clamp(): smooth growth from laptop (~1280px) to large monitor (1920px+). */
.terms-page {
  font-size: clamp(14px, 1vw, 19px);
  line-height: 1.6;
  color: var(--text-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.terms-shell {
  padding: 24px clamp(16px, 2.5vw, 64px);
  max-width: clamp(960px, 95vw, 2400px);
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Apple SD Gothic Neo", sans-serif;
  color: var(--text-primary);
}
.terms-header {
  margin-bottom: 16px;
}
/* !important needed to override element-ui white-dashboard .content layer
   which forces a lighter text color on body content. */
.terms-page .page-title {
  font-size: clamp(20px, 1.8vw, 32px);
  font-weight: 700;
  color: var(--gray-9) !important;
  margin: 0 0 4px;
}
.terms-page .page-sub {
  font-size: clamp(13px, 1vw, 18px);
  color: var(--text-muted) !important;
  margin: 0;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}
.toolbar-l {
  display: flex;
  gap: 8px;
}
.terms-alert {
  margin-bottom: 16px;
}

/* Title row */
.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  background: white;
  border: 1px solid var(--gray-3);
  border-radius: 6px;
  padding: 12px 16px;
  box-shadow: var(--shadow-card);
}
.title-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  width: 60px;
  flex-shrink: 0;
}
.title-input {
  flex: 1;
  padding: 8px 12px;
  font-size: var(--font-size-base);
  border: 1px solid var(--gray-4);
  border-radius: 4px;
  outline: none;
  background: white;
  color: var(--text-primary);
}
.title-input:focus {
  border-color: var(--primary);
}
.title-input:disabled {
  background: var(--gray-2);
  color: var(--text-muted);
}

/* Buttons */
.btn {
  padding: 9px 18px;
  font-size: var(--font-size-base);
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid var(--gray-4);
  background: white;
  color: var(--text-secondary);
}
.btn:hover:not(:disabled) {
  border-color: var(--gray-5);
  color: var(--text-primary);
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  color: white;
}
.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  color: white;
}
/* Destructive primary — used in delete dialog confirm button. */
.btn-primary.danger-primary {
  background: var(--red);
  border-color: var(--red);
}
.btn-primary.danger-primary:hover:not(:disabled) {
  background: var(--red-hover);
  border-color: var(--red-hover);
}
/* Link-style text button — color/weight give clear affordance even before hover. */
.btn-text {
  background: transparent;
  border: none;
  color: var(--primary);
  padding: 6px 8px;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.btn-text:hover:not(:disabled) {
  color: var(--primary-hover);
  background: var(--primary-bg);
}
.btn-text.danger {
  color: var(--red);
}
.btn-text.danger:hover:not(:disabled) {
  color: var(--red-hover);
  background: var(--red-bg);
}
.btn-text:disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}
.btn-sm {
  padding: 5px 10px;
  font-size: var(--font-size-sm);
}

/* Status dot */
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  white-space: nowrap;
}
.status::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-5);
  display: inline-block;
}
.status.published::before {
  background: var(--green);
}
.status.never-published::before {
  background: var(--gray-5);
}
.status.publishing::before {
  background: var(--orange);
}
.status.failed::before {
  background: var(--red);
}
.status.failed {
  color: var(--red);
}
.muted {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}
/* Version display — label first (1.7), 정수 v숫자 보조 (v8). */
.v-main {
  font-weight: 600;
  color: var(--text-primary);
}
.v-sub {
  margin-left: 6px;
  color: var(--text-muted);
  font-size: clamp(11px, 0.7vw, 13px);
}

/* Card */
.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
.card {
  background: white;
  border: 1px solid var(--gray-3);
  border-radius: 6px;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
}
.card-header {
  padding: 14px 20px;
  border-bottom: 1px solid var(--gray-3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
}
.card-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
}
.card-meta {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.card-toolbar {
  padding: 8px 12px;
  border-bottom: 1px solid var(--gray-3);
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  background: var(--gray-1);
}
.card-toolbar .btn-sm {
  padding: 6px 10px;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}
/* Editor body — 한글 약관 본문 가독성 우선. clamp 으로 viewport 에 비례하되 4K 모니터까지 자연스럽게 커짐. */
.editor-area {
  min-height: 480px;
  max-height: 600px;
  overflow-y: auto;
  padding: 20px;
  font-size: clamp(15px, 1.15vw, 22px);
  line-height: var(--line-height-loose);
  outline: none;
  background: white;
  color: var(--text-primary);
}
.editor-area:focus {
  outline: none;
  background: white;
}
.preview-frame {
  width: 100%;
  height: 540px;
  border: none;
  background: var(--gray-1);
}
.card-footer-meta {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  padding: 10px 20px;
  border-top: 1px solid var(--gray-3);
  background: white;
  word-break: break-all;
}
.card-footer-meta code {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

/* Sticky action bar */
.sticky-actions {
  position: sticky;
  bottom: 0;
  background: white;
  border: 1px solid var(--gray-3);
  padding: 12px 20px;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: var(--shadow-card);
  margin-top: 16px;
  z-index: 10;
}
.sticky-meta {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.sticky-actions .group {
  display: flex;
  gap: 8px;
}

/* Dirty indicator — color palette intentionally distinct from `.status.*`
   (publish status dot) so the operator does not confuse "unsaved changes"
   with the publish lifecycle state. */
.save-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.save-state .save-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-5);
}
.save-state.is-saved .save-dot {
  background: var(--gray-6);
}
.save-state.is-dirty .save-dot {
  background: var(--orange);
}
.save-state.is-dirty {
  color: var(--text-secondary);
  font-weight: 500;
}

/* Dialog inner helpers */
.dialog-paragraph {
  margin: 0 0 16px;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
/* H2 — version transition pill above the publish form. Slightly stronger
   weight/color than .dialog-paragraph so the operator immediately sees what
   version label will be applied. */
.dialog-transition {
  margin: 0 0 16px;
  padding: 8px 12px;
  background: var(--gray-1);
  border: 1px solid var(--gray-3);
  border-radius: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: 500;
}
.dialog-hint {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  margin-top: 4px;
}
.dialog-warning {
  margin: 0 0 12px;
  padding: 8px 12px;
  background: var(--red-bg);
  border: 1px solid var(--red);
  border-radius: 4px;
  font-size: var(--font-size-sm);
  color: var(--red);
  font-weight: 500;
}
.dialog-progress {
  text-align: center;
  color: var(--text-muted);
  margin-top: 8px;
  font-size: var(--font-size-sm);
}
</style>
<style>
/* Unscoped: dialog (mockup look) */
.terms-dialog .el-dialog__header {
  padding: 18px 24px;
  border-bottom: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body {
  padding: 24px;
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  color: var(--text-primary);
}
.terms-dialog .el-dialog__body label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}
.terms-dialog .el-dialog__body .el-input__inner {
  font-size: var(--font-size-base);
}
.terms-dialog .el-dialog__footer {
  padding: 14px 24px;
  border-top: 1px solid var(--gray-3);
}
.terms-dialog .el-dialog__footer .btn + .btn {
  margin-left: 8px;
}
</style>
